/**
 * Outil « Cercle trigonométrique » (Boîte à outils, 2nde → prépa).
 *
 * L'élève fait tourner le point M au doigt (plusieurs tours, sens horaire
 * compris) ; cos x, sin x et tan x se lisent en projection. Près d'un angle
 * remarquable (multiples de π/6 et π/4), le point s'aimante et les valeurs
 * deviennent EXACTES (√2/2). Options : angles associés, courbes déroulées,
 * défi « place l'angle ». Calculs purs dans `trig-values.ts`.
 */

import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { clamp } from '../../ui/frame';
import {
  ASSOCIATED_ANGLES,
  CHALLENGE_ROUNDS,
  STEP,
  degreeLabel,
  exactValues,
  formatApprox,
  isOnTarget,
  isRemarkableStep,
  pickTarget,
  principalStep,
  radianLabel,
  REMARKABLE_STEPS,
  snapStep,
  unwrapAngle,
  type ExactValue,
} from './trig-values';

const VIEW = 1.45; // demi-côté du viewBox, en unités du cercle
const COS_COLOR = 'var(--color-primary-500)';
const SIN_COLOR = 'var(--color-accent-500)';
const TAN_COLOR = 'var(--color-xp-500)';
const AXIS = 'var(--color-ink-300)';
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-400)';

/** pas remarquable suivant (dir = 1) ou précédent (dir = −1) */
function nextRemarkable(theta: number, dir: 1 | -1): number {
  let k = Math.round(theta / STEP);
  if (Math.abs(k * STEP - theta) > 1e-9 || !isRemarkableStep(k)) {
    k = dir > 0 ? Math.floor(theta / STEP) : Math.ceil(theta / STEP);
  }
  do k += dir;
  while (!isRemarkableStep(k));
  return k;
}

interface Challenge {
  round: number;
  target: number;
  score: number;
  result: 'ok' | 'ko' | null;
}

export function TrigCircleTool({
  onSend,
  sendCost,
  rng = Math.random,
}: ToolProps & { rng?: () => number }) {
  const [theta, setTheta] = useState((4 * Math.PI) / 12); // π/3
  const [unit, setUnit] = useState<'rad' | 'deg'>('rad');
  const [showAssoc, setShowAssoc] = useState(false);
  const [showCurves, setShowCurves] = useState(false);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);

  const k = snapStep(theta);
  const shown = k !== null ? k * STEP : theta;
  const exact = k !== null ? exactValues(k) : null;
  const c = Math.cos(shown);
  const s = Math.sin(shown);
  const cosZero = Math.abs(c) < 1e-9;
  const t = cosZero ? null : s / c;
  const hideAngle = challenge !== null && challenge.result === null;

  const angleAt = (e: PointerEvent<SVGSVGElement>): number | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const x = ((e.clientX - r.left) / r.width) * 2 * VIEW - VIEW;
    const y = VIEW - ((e.clientY - r.top) / r.height) * 2 * VIEW;
    if (Math.hypot(x, y) < 0.08) return null;
    return Math.atan2(y, x);
  };

  const follow = (e: PointerEvent<SVGSVGElement>) => {
    const raw = angleAt(e);
    if (raw !== null) setTheta((prev) => unwrapAngle(prev, raw));
  };

  const release = () => {
    if (!dragging.current) return;
    dragging.current = false;
    // l'aimant fixe la valeur exacte : le prochain geste repart de là
    setTheta((prev) => {
      const kk = snapStep(prev);
      return kk !== null ? kk * STEP : prev;
    });
  };

  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      setTheta((prev) => nextRemarkable(prev, 1) * STEP);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      setTheta((prev) => nextRemarkable(prev, -1) * STEP);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setTheta(0);
    }
  };

  const angleText = (() => {
    if (k === null) return `≈ ${formatApprox(shown, 2)} rad · ${degreeLabel(shown)}`;
    const rad = radianLabel(k).plain;
    const deg = `${k * 15}°`;
    return unit === 'rad' ? `${rad} · ${deg}` : `${deg} · ${rad}`;
  })();

  const startChallenge = () => {
    setChallenge({ round: 1, target: pickTarget(rng), score: 0, result: null });
  };
  const validate = () => {
    if (!challenge || challenge.result) return;
    const ok = isOnTarget(k, challenge.target);
    setChallenge({ ...challenge, result: ok ? 'ok' : 'ko', score: challenge.score + (ok ? 1 : 0) });
  };
  const nextRound = () => {
    if (!challenge) return;
    if (challenge.round >= CHALLENGE_ROUNDS) return;
    setChallenge({
      round: challenge.round + 1,
      target: pickTarget(rng, challenge.target),
      score: challenge.score,
      result: null,
    });
  };
  const finished = challenge !== null && challenge.round >= CHALLENGE_ROUNDS && !!challenge.result;

  return (
    <div className="space-y-4">
      {/* réglages */}
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Unité d’angle"
          value={unit}
          options={[
            { value: 'rad', label: 'Radians' },
            { value: 'deg', label: 'Degrés' },
          ]}
          onChange={setUnit}
        />
        <ToggleChip on={showAssoc} onClick={() => setShowAssoc((v) => !v)}>
          Angles associés
        </ToggleChip>
        <ToggleChip on={showCurves} onClick={() => setShowCurves((v) => !v)}>
          Courbes
        </ToggleChip>
        <ToggleChip
          on={challenge !== null}
          onClick={() => (challenge ? setChallenge(null) : startChallenge())}
        >
          Défi
        </ToggleChip>
      </div>

      {challenge && (
        <ChallengeBanner
          challenge={challenge}
          finished={finished}
          onValidate={validate}
          onNext={nextRound}
          onRestart={startChallenge}
          onQuit={() => setChallenge(null)}
        />
      )}

      {/* le cercle */}
      <div className="mx-auto w-full max-w-[22rem]">
        <svg
          ref={svgRef}
          viewBox={`${-VIEW} ${-VIEW} ${2 * VIEW} ${2 * VIEW}`}
          className="focus-visible:ring-primary-400 block aspect-square w-full touch-none select-none rounded-2xl outline-none focus-visible:ring-2"
          role="slider"
          tabIndex={0}
          aria-label="Angle x sur le cercle trigonométrique"
          aria-valuemin={-1000}
          aria-valuemax={1000}
          aria-valuenow={Math.round((shown * 180) / Math.PI)}
          aria-valuetext={hideAngle ? 'angle masqué pendant le défi' : angleText}
          onPointerDown={(e) => {
            dragging.current = true;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            follow(e);
          }}
          onPointerMove={(e) => {
            if (dragging.current) follow(e);
          }}
          onPointerUp={release}
          onPointerCancel={release}
          onKeyDown={onKeyDown}
        >
          {/* axes et cercle */}
          <line x1={-VIEW} y1={0} x2={VIEW} y2={0} stroke={AXIS} strokeWidth={0.012} />
          <line x1={0} y1={-VIEW} x2={0} y2={VIEW} stroke={AXIS} strokeWidth={0.012} />
          <circle cx={0} cy={0} r={1} fill="none" stroke={MUTED} strokeWidth={0.014} />
          {/* droite des tangentes */}
          <line
            x1={1}
            y1={-VIEW}
            x2={1}
            y2={VIEW}
            stroke={TAN_COLOR}
            strokeWidth={0.01}
            strokeDasharray="0.04 0.04"
          />
          {REMARKABLE_STEPS.map((rk) => (
            <circle
              key={rk}
              cx={Math.cos(rk * STEP)}
              cy={-Math.sin(rk * STEP)}
              r={0.02}
              fill={MUTED}
            />
          ))}
          <text x={VIEW - 0.06} y={-0.05} textAnchor="end" fontSize={0.1} fill={COS_COLOR}>
            cos
          </text>
          <text x={0.05} y={-VIEW + 0.12} fontSize={0.1} fill={SIN_COLOR}>
            sin
          </text>
          <text x={1.04} y={-VIEW + 0.12} fontSize={0.1} fill={TAN_COLOR}>
            tan
          </text>

          {/* angles associés */}
          {showAssoc &&
            ASSOCIATED_ANGLES.map((a) => {
              const ax = a.of(shown);
              return (
                <g key={a.id} opacity={0.75}>
                  <line
                    x1={0}
                    y1={0}
                    x2={Math.cos(ax)}
                    y2={-Math.sin(ax)}
                    stroke={MUTED}
                    strokeWidth={0.01}
                    strokeDasharray="0.03 0.03"
                  />
                  <circle cx={Math.cos(ax)} cy={-Math.sin(ax)} r={0.035} fill={MUTED} />
                  <text
                    x={1.22 * Math.cos(ax)}
                    y={-1.22 * Math.sin(ax) + 0.035}
                    textAnchor="middle"
                    fontSize={0.085}
                    fill={MUTED}
                  >
                    {a.label}
                  </text>
                </g>
              );
            })}

          {/* arc de l'angle */}
          <path d={arcPath(shown, 0.22)} fill="none" stroke={INK} strokeWidth={0.012} />

          {/* tangente */}
          {t !== null && (
            <>
              <line
                x1={0}
                y1={0}
                x2={1}
                y2={-clamp(t, -VIEW, VIEW)}
                stroke={TAN_COLOR}
                strokeWidth={0.008}
                opacity={0.6}
              />
              <line
                x1={1}
                y1={0}
                x2={1}
                y2={-clamp(t, -VIEW, VIEW)}
                stroke={TAN_COLOR}
                strokeWidth={0.035}
                strokeLinecap="round"
              />
            </>
          )}

          {/* projections */}
          <line
            x1={c}
            y1={-s}
            x2={c}
            y2={0}
            stroke={COS_COLOR}
            strokeWidth={0.01}
            strokeDasharray="0.03 0.03"
          />
          <line
            x1={c}
            y1={-s}
            x2={0}
            y2={-s}
            stroke={SIN_COLOR}
            strokeWidth={0.01}
            strokeDasharray="0.03 0.03"
          />
          <line
            x1={0}
            y1={0}
            x2={c}
            y2={0}
            stroke={COS_COLOR}
            strokeWidth={0.04}
            strokeLinecap="round"
          />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={-s}
            stroke={SIN_COLOR}
            strokeWidth={0.04}
            strokeLinecap="round"
          />

          {/* rayon et point M */}
          <line x1={0} y1={0} x2={c} y2={-s} stroke={INK} strokeWidth={0.016} />
          {challenge?.result === 'ko' && (
            <circle
              cx={Math.cos(challenge.target * STEP)}
              cy={-Math.sin(challenge.target * STEP)}
              r={0.07}
              fill="none"
              stroke={TAN_COLOR}
              strokeWidth={0.025}
            />
          )}
          <circle cx={c} cy={-s} r={0.11} fill={INK} opacity={0.12} />
          <circle cx={c} cy={-s} r={0.055} fill={k !== null ? COS_COLOR : INK} />
          {!hideAngle && (
            <text
              x={1.25 * c}
              y={-1.25 * s + 0.035}
              textAnchor="middle"
              fontSize={0.1}
              fontWeight={600}
              fill={INK}
            >
              {k !== null ? (unit === 'rad' ? radianLabel(k).plain : `${k * 15}°`) : ''}
            </text>
          )}
        </svg>
      </div>

      {showCurves && <UnrolledCurves theta={shown} />}

      {/* valeurs */}
      <section aria-label="Valeurs" className="space-y-2">
        {!hideAngle && (
          <p
            className="text-ink-900 font-display flex flex-wrap items-baseline gap-x-2 text-lg font-bold"
            data-testid="trig-angle"
          >
            {k !== null ? (
              <>
                <MathText className="text-xl">{`$\\displaystyle x = ${unit === 'rad' ? radianLabel(k).tex : `${k * 15}^\\circ`}$`}</MathText>
                <span className="text-ink-500 text-sm font-medium">
                  {unit === 'rad' ? `soit ${k * 15}°` : `soit ${radianLabel(k).plain} rad`}
                </span>
              </>
            ) : (
              <>
                <span>x ≈ {formatApprox(shown, 2)} rad</span>
                <span className="text-ink-500 text-sm font-medium">soit {degreeLabel(shown)}</span>
              </>
            )}
          </p>
        )}
        {!hideAngle && k !== null && principalStep(k) !== k && (
          <p className="text-ink-500 text-sm">
            Mesure principale : <MathText>{`$${radianLabel(principalStep(k)).tex}$`}</MathText>
          </p>
        )}
        <ValueRow
          name="cos x"
          color={COS_COLOR}
          exact={exact?.cos ?? null}
          approx={c}
          onSend={onSend}
          sendCost={sendCost}
        />
        <ValueRow
          name="sin x"
          color={SIN_COLOR}
          exact={exact?.sin ?? null}
          approx={s}
          onSend={onSend}
          sendCost={sendCost}
        />
        <ValueRow
          name="tan x"
          color={TAN_COLOR}
          exact={exact ? exact.tan : null}
          approx={t}
          undefinedValue={cosZero}
          onSend={onSend}
          sendCost={sendCost}
        />
        {k === null && (
          <p className="text-ink-500 text-sm">
            Approche le point d’un repère gris : il s’aimante sur un angle remarquable et les
            valeurs deviennent exactes.
          </p>
        )}
      </section>

      {showAssoc && (
        <section
          aria-label="Angles associés"
          className="bg-ink-50 space-y-1 rounded-2xl p-3 text-sm"
        >
          {ASSOCIATED_ANGLES.map((a) => (
            <p key={a.id} className="text-ink-700">
              <span className="text-ink-900 font-semibold">{a.label}</span> : {a.cosRule} ·{' '}
              {a.sinRule}
            </p>
          ))}
        </section>
      )}
    </div>
  );
}

/** Arc de 0 à l'angle (sens direct si positif), en coordonnées écran (y vers le bas). */
export function arcPath(angle: number, r: number): string {
  const twoPi = 2 * Math.PI;
  if (Math.abs(angle) < 1e-9) return '';
  let rem = angle % twoPi;
  if (Math.abs(rem) < 1e-9) rem = angle > 0 ? twoPi - 1e-4 : -(twoPi - 1e-4);
  const x = r * Math.cos(rem);
  const y = -r * Math.sin(rem);
  const large = Math.abs(rem) > Math.PI ? 1 : 0;
  const sweep = rem > 0 ? 0 : 1;
  return `M ${r} 0 A ${r} ${r} 0 ${large} ${sweep} ${x.toFixed(4)} ${y.toFixed(4)}`;
}

function ValueRow({
  name,
  color,
  exact,
  approx,
  undefinedValue = false,
  onSend,
  sendCost,
}: {
  name: string;
  color: string;
  exact: ExactValue | null;
  approx: number | null;
  undefinedValue?: boolean;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  return (
    <div className="border-ink-100 flex min-h-12 items-center gap-3 rounded-2xl border px-3 py-1.5">
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
      <span className="text-ink-600 w-12 shrink-0 text-sm font-semibold">{name}</span>
      <span className="text-ink-900 min-w-0 flex-1 text-base font-semibold">
        {undefinedValue ? (
          <span className="text-ink-500 text-sm font-medium">n’existe pas (cos x = 0)</span>
        ) : exact ? (
          <MathText className="text-lg">{`$\\displaystyle ${exact.tex}$`}</MathText>
        ) : approx !== null ? (
          `≈ ${formatApprox(approx)}`
        ) : null}
      </span>
      {onSend && exact && !undefinedValue && (
        <SendToSheetButton
          label={exact.plain}
          cost={sendCost}
          onClick={() => onSend({ atoms: exact.atoms })}
        />
      )}
    </div>
  );
}

function ChallengeBanner({
  challenge,
  finished,
  onValidate,
  onNext,
  onRestart,
  onQuit,
}: {
  challenge: Challenge;
  finished: boolean;
  onValidate: () => void;
  onNext: () => void;
  onRestart: () => void;
  onQuit: () => void;
}) {
  const target = radianLabel(challenge.target);
  return (
    <section
      aria-live="polite"
      className="bg-xp-50 space-y-2 rounded-2xl p-3"
      data-testid="trig-challenge"
    >
      <p className="text-ink-900 font-display text-base font-bold">
        Défi {challenge.round}/{CHALLENGE_ROUNDS} · Place{' '}
        <MathText>{`$x = ${target.tex}$`}</MathText>
      </p>
      {challenge.result === null && (
        <button
          type="button"
          onClick={onValidate}
          className={cn(PRIMARY_BUTTON, 'w-full sm:w-auto')}
        >
          Valider
        </button>
      )}
      {challenge.result !== null && (
        <>
          <p className="text-ink-700 text-sm font-semibold">
            {challenge.result === 'ok'
              ? 'Bravo, c’est bien placé !'
              : 'Pas tout à fait : l’angle cherché est entouré en vert.'}
          </p>
          {finished ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <p className="text-ink-900 font-bold">
                Score : {challenge.score}/{CHALLENGE_ROUNDS}
              </p>
              <button
                type="button"
                onClick={onRestart}
                className={cn(PRIMARY_BUTTON, 'w-full sm:w-auto')}
              >
                Rejouer
              </button>
              <button
                type="button"
                onClick={onQuit}
                className={cn(SECONDARY_BUTTON, 'w-full sm:w-auto')}
              >
                Fin du défi
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className={cn(PRIMARY_BUTTON, 'w-full sm:w-auto')}
            >
              Angle suivant
            </button>
          )}
        </>
      )}
    </section>
  );
}

/** cos et sin déroulés sur [0 ; 2π], avec la position de x. */
function UnrolledCurves({ theta }: { theta: number }) {
  const W = 6.9;
  const X0 = 0.35;
  const sx = (W - X0 - 0.15) / (2 * Math.PI);
  const paths = useMemo(() => {
    const pts = (f: (x: number) => number) =>
      Array.from({ length: 97 }, (_, i) => {
        const x = (i / 96) * 2 * Math.PI;
        return `${i === 0 ? 'M' : 'L'} ${(X0 + x * sx).toFixed(3)} ${(-f(x)).toFixed(3)}`;
      }).join(' ');
    return { cos: pts(Math.cos), sin: pts(Math.sin) };
  }, [sx]);
  const m = ((theta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const mx = X0 + m * sx;
  return (
    <svg
      viewBox={`0 -1.35 ${W} 2.75`}
      className="mx-auto block w-full max-w-[26rem]"
      role="img"
      aria-label="Courbes de cosinus et sinus déroulées de 0 à 2π"
    >
      <line x1={X0} y1={0} x2={W - 0.1} y2={0} stroke={AXIS} strokeWidth={0.015} />
      <line x1={X0} y1={-1.2} x2={X0} y2={1.2} stroke={AXIS} strokeWidth={0.015} />
      <path d={paths.cos} fill="none" stroke={COS_COLOR} strokeWidth={0.035} />
      <path d={paths.sin} fill="none" stroke={SIN_COLOR} strokeWidth={0.035} />
      <line x1={mx} y1={-1.2} x2={mx} y2={1.2} stroke={INK} strokeWidth={0.015} />
      <circle cx={mx} cy={-Math.cos(m)} r={0.07} fill={COS_COLOR} />
      <circle cx={mx} cy={-Math.sin(m)} r={0.07} fill={SIN_COLOR} />
      {[
        [0, '0'],
        [Math.PI, 'π'],
        [2 * Math.PI, '2π'],
      ].map(([v, label]) => (
        <text
          key={label as string}
          x={X0 + (v as number) * sx}
          y={1.34}
          textAnchor="middle"
          fontSize={0.26}
          fill={MUTED}
        >
          {label}
        </text>
      ))}
    </svg>
  );
}
